/**
 * useDailyChecksConfig.js
 *
 * FT-80: Hook for managing Daily Checks configuration in Settings.
 * Allows enabling/disabling default categories and editing check items.
 * Stores customizations in vessel-scoped storage.
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import {
  DEFAULT_DAILY_CHECKS,
  DAILY_CHECK_CATEGORIES,
  DAILY_CHECK_CATEGORY_LABELS,
  DAILY_CHECK_CATEGORY_ICONS
} from '../../../config/operations/dailyChecksLibrary';

const STORAGE_KEY = 'operations_daily_checks_config';

/**
 * Hook for managing Daily Checks configuration
 * Supports: enabling/disabling categories, editing items within categories, adding custom categories
 */
export const useDailyChecksConfig = () => {
  const { value: savedConfig, setValue: saveConfig } = useStorageState(STORAGE_KEY, null);

  // Merge default categories with saved customizations
  const categories = useMemo(() => {
    const defaultCategories = Object.entries(DEFAULT_DAILY_CHECKS).map(([id, checks]) => ({
      id,
      label: DAILY_CHECK_CATEGORY_LABELS[id] || id,
      icon: DAILY_CHECK_CATEGORY_ICONS[id] || 'ClipboardList',
      enabled: true,
      isDefault: true,
      checks: checks.map(c => ({ ...c }))
    }));

    if (!savedConfig?.categories) {
      return defaultCategories;
    }

    // Merge saved config over defaults
    // FT-80 Phase 12: Deep copy checks array to prevent shared reference mutation bug
    return defaultCategories.map(defaultCat => {
      const savedCat = savedConfig.categories.find(c => c.id === defaultCat.id);
      if (savedCat) {
        // CRITICAL: Create deep copy of checks to prevent shared reference bug
        // (editing one category was affecting another because they shared the same array)
        const checksToUse = savedCat.checks || defaultCat.checks;
        return {
          ...defaultCat,
          enabled: savedCat.enabled ?? true,
          checks: checksToUse.map(c => ({ ...c }))
        };
      }
      return defaultCat;
    }).concat(
      // Add any custom categories (exclude any that match default category IDs - FT-80 Phase 11 dedup fix)
      (savedConfig.categories || []).filter(c =>
        !c.isDefault && !Object.keys(DEFAULT_DAILY_CHECKS).includes(c.id)
      )
    );
  }, [savedConfig]);

  // Toggle category enabled/disabled
  const toggleCategory = useCallback((categoryId) => {
    const current = savedConfig?.categories || [];
    const existing = current.find(c => c.id === categoryId);

    let updated;
    if (existing) {
      updated = current.map(c =>
        c.id === categoryId ? { ...c, enabled: !c.enabled } : c
      );
    } else {
      // FT-113: First time toggling a default - create entry WITH checks
      // This ensures checks are saved (not just referenced from library)
      const defaultCat = categories.find(c => c.id === categoryId);
      updated = [...current, {
        id: categoryId,
        enabled: false,
        isDefault: defaultCat?.isDefault,
        // FT-113 FIX: Include checks array so edits are properly persisted
        checks: defaultCat?.checks?.map(c => ({ ...c })) || []
      }];
    }

    saveConfig({ ...savedConfig, categories: updated });
  }, [savedConfig, saveConfig, categories]);

  // Update checks for a category
  const updateCategoryChecks = useCallback((categoryId, checks) => {
    const current = savedConfig?.categories || [];
    const existing = current.find(c => c.id === categoryId);

    // Check if this is a default category (to preserve isDefault flag)
    const isDefaultCategory = Object.keys(DEFAULT_DAILY_CHECKS).includes(categoryId);

    let updated;
    if (existing) {
      updated = current.map(c =>
        c.id === categoryId ? { ...c, checks } : c
      );
    } else {
      // FT-80 Phase 8: Include isDefault flag to prevent duplication bug
      updated = [...current, { id: categoryId, enabled: true, checks, isDefault: isDefaultCategory }];
    }

    saveConfig({ ...savedConfig, categories: updated });
  }, [savedConfig, saveConfig]);

  // Add custom category
  const addCategory = useCallback((label) => {
    const id = `custom_${Date.now()}`;
    const current = savedConfig?.categories || [];
    const updated = [...current, {
      id,
      label,
      enabled: true,
      isDefault: false,
      checks: []
    }];
    saveConfig({ ...savedConfig, categories: updated });
    return id;
  }, [savedConfig, saveConfig]);

  // Get enabled categories only (for use in useDailyChecks)
  const getEnabledCategories = useCallback(() => {
    return categories.filter(c => c.enabled);
  }, [categories]);

  // Get categories formatted for Settings checklist display
  const getCategoriesForSettings = useCallback(() => {
    return categories.map(cat => ({
      id: cat.id,
      name: cat.label,
      description: `${cat.checks.length} routine checks`,
      icon: cat.icon,
      items: cat.checks.map(check => ({
        id: check.id,
        text: check.text,
        required: check.required
      })),
      isCustomisable: true,
      regulatory: 'SMS Best Practice',
      enabled: cat.enabled,
      isDefault: cat.isDefault
    }));
  }, [categories]);

  // Reset a single category to its default checks
  const resetCategory = useCallback((categoryId) => {
    const defaultChecks = DEFAULT_DAILY_CHECKS[categoryId];
    if (!defaultChecks) return; // Can't reset custom categories

    const current = savedConfig?.categories || [];
    const existing = current.find(c => c.id === categoryId);

    let updated;
    if (existing) {
      updated = current.map(c =>
        c.id === categoryId ? { ...c, checks: defaultChecks.map(check => ({ ...check })) } : c
      );
    } else {
      updated = [...current, { id: categoryId, enabled: true, checks: defaultChecks.map(check => ({ ...check })) }];
    }

    saveConfig({ ...savedConfig, categories: updated });
  }, [savedConfig, saveConfig]);

  // Reset all categories to defaults (clears all customizations)
  const resetAllToDefaults = useCallback(() => {
    saveConfig(null);
  }, [saveConfig]);

  return {
    categories,
    toggleCategory,
    updateCategoryChecks,
    addCategory,
    getEnabledCategories,
    getCategoriesForSettings,
    resetCategory,
    resetAllToDefaults
  };
};

export default useDailyChecksConfig;
